import { useState, useEffect, useRef } from 'react';
import { Plane, Check, Sparkles } from 'lucide-react';

const ROSTER = [
  { d: '01 Mar', f: 'EK 29', fr: 'DXB', to: 'LHR', dep: '07:30', arr: '12:15' },
  { d: '02 Mar', f: 'EK 30', fr: 'LHR', to: 'DXB', dep: '21:05', arr: '06:40' },
  { d: '03 Mar', f: 'OFF', fr: '', to: '', dep: '', arr: '', off: true },
  { d: '05 Mar', f: 'EK 384', fr: 'DXB', to: 'BKK', dep: '03:15', arr: '12:30' },
  { d: '06 Mar', f: 'LYR', fr: 'BKK', to: '', dep: '', arr: '', lyr: true },
  { d: '07 Mar', f: 'EK 385', fr: 'BKK', to: 'DXB', dep: '01:50', arr: '05:20' },
  { d: '08 Mar', f: 'OFF', fr: '', to: '', dep: '', arr: '', off: true },
  { d: '09 Mar', f: 'EK 201', fr: 'DXB', to: 'JFK', dep: '08:00', arr: '14:20' },
];

const FLIGHTS = [
  { from: 'DXB', to: 'LHR', num: 'EK 29' },
  { from: 'LHR', to: 'DXB', num: 'EK 30' },
  { from: 'DXB', to: 'BKK', num: 'EK 384' },
  { from: 'BKK', to: 'DXB', num: 'EK 385' },
];

type Phase = 'roster' | 'flash' | 'scan' | 'flights' | 'done';

interface Props {
  active: boolean;
}

export function ImportSlide({ active }: Props) {
  const [phase, setPhase] = useState<Phase>('roster');
  const [flights, setFlights] = useState(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!active) return;

    // Reset state when slide becomes active
    setPhase('roster');
    setFlights(0);
    timers.current.forEach(clearTimeout);
    timers.current = [];

    const t = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };

    t(() => setPhase('flash'), 1400);
    t(() => setPhase('scan'), 1800);
    t(() => setPhase('flights'), 3200);
    for (let i = 0; i < 4; i++) t(() => setFlights((v) => v + 1), 3600 + i * 300);
    t(() => setPhase('done'), 5200);

    return () => timers.current.forEach(clearTimeout);
  }, [active]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8">
      {/* Phone mockup */}
      <div
        className="relative mb-8"
        style={{ animation: active ? 'ob-fade-in 0.6s ease-out both' : undefined }}
      >
        <PhoneFrame>
          {/* Roster view */}
          {(phase === 'roster' || phase === 'flash') && (
            <div className="absolute inset-0 bg-[#0B1426]">
              <RosterTable />
            </div>
          )}

          {/* Flash effect */}
          {phase === 'flash' && (
            <div
              className="absolute inset-0 bg-white z-30 pointer-events-none"
              style={{ animation: 'ob-flash 0.4s ease-out forwards' }}
            />
          )}

          {/* Scan + flight extraction */}
          {(phase === 'scan' || phase === 'flights' || phase === 'done') && (
            <div className="absolute inset-0 bg-slate-900 flex flex-col" style={{ animation: 'ob-phone-fade 0.3s ease-out' }}>
              {/* App header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/30">
                <div className="w-5 h-5 rounded-md bg-accent/20 flex items-center justify-center">
                  <span className="text-[8px] font-extrabold text-accent">R</span>
                </div>
                <span className="text-[10px] font-bold text-slate-200">Rost & Found</span>
              </div>

              {/* Scan bar */}
              {phase === 'scan' && (
                <div className="relative h-14 bg-[#0B1426]/50 border-b border-slate-700/20 overflow-hidden">
                  <div className="transform scale-[0.3] origin-top-left" style={{ width: '333%' }}>
                    <MiniRoster />
                  </div>
                  <div
                    className="absolute left-0 right-0 h-0.5"
                    style={{
                      background: 'linear-gradient(90deg, transparent, var(--color-accent), transparent)',
                      boxShadow: '0 0 8px var(--color-accent)',
                      animation: 'ob-scan 1.4s ease-in-out infinite',
                    }}
                  />
                </div>
              )}

              {/* Status + flights */}
              <div className="flex-1 p-2.5">
                {phase === 'scan' && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-2.5 h-2.5 rounded-full border border-accent border-t-transparent animate-spin" />
                    <span className="text-[8px] text-slate-400 font-semibold">Extracting flights...</span>
                  </div>
                )}
                {(phase === 'flights' || phase === 'done') && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-3.5 h-3.5 rounded-full bg-sage/20 flex items-center justify-center">
                      <Check size={7} className="text-sage" />
                    </div>
                    <span className="text-[8px] text-sage font-bold">
                      {phase === 'done' ? '4 flights imported' : `${flights} flights found...`}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  {FLIGHTS.map((f, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-accent/[0.04] border border-accent/[0.06]"
                      style={{
                        opacity: i < flights ? 1 : 0,
                        transform: i < flights ? 'translateX(0)' : 'translateX(-6px)',
                        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                      }}
                    >
                      <Plane size={8} className="text-accent/30 shrink-0" />
                      <span className="text-[8px] font-bold text-slate-300">{f.from}</span>
                      <span className="text-[7px] text-accent/40">&rarr;</span>
                      <span className="text-[8px] font-bold text-slate-300">{f.to}</span>
                      <span className="text-[7px] text-slate-500 ml-auto">{f.num}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </PhoneFrame>
      </div>

      {/* Text */}
      <div className="flex items-center gap-1.5 mb-2" style={{ animation: active ? 'ob-slide-up 0.5s ease-out 0.2s both' : undefined }}>
        <Sparkles size={14} className="text-accent/60" />
        <span className="text-[11px] text-accent/60 font-semibold uppercase tracking-wider">Powered by AI</span>
      </div>
      <h1
        className="text-[28px] leading-tight font-extrabold text-slate-100 mb-3 text-center"
        style={{ animation: active ? 'ob-slide-up 0.6s ease-out 0.3s both' : undefined }}
      >
        Snap. Parse. Done.
      </h1>
      <p
        className="text-[15px] text-slate-400 text-center max-w-[280px] leading-relaxed"
        style={{ animation: active ? 'ob-slide-up 0.6s ease-out 0.5s both' : undefined }}
      >
        Screenshot or photograph your roster — AI extracts every flight instantly.
      </p>
    </div>
  );
}

// ── Sub-components ──

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative rounded-[32px] border-2 border-slate-700/40 bg-slate-900 overflow-hidden"
      style={{
        width: 220, height: 440,
        boxShadow: '0 0 80px rgba(201,160,220,0.06), 0 20px 60px rgba(0,0,0,0.3)',
      }}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-b-xl z-40" />
      <div className="h-7 flex items-end justify-between px-5 pb-0.5 text-[7px] font-semibold text-slate-400 relative z-30">
        <span>9:41</span>
        <span style={{ fontSize: 6, opacity: 0.4 }}>5G</span>
      </div>
      <div className="absolute inset-0 top-7 overflow-hidden">{children}</div>
    </div>
  );
}

function RosterTable() {
  return (
    <div className="bg-[#0B1426]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.05]">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-amber-500/80 flex items-center justify-center">
            <span className="text-[7px] font-extrabold text-[#0B1426]">EK</span>
          </div>
          <span className="text-[9px] font-bold text-amber-100/70">CrewConnect</span>
        </div>
        <span className="text-[8px] text-amber-100/25 font-semibold">MAR 2026</span>
      </div>
      <div className="px-2 py-1">
        <div className="grid grid-cols-6 gap-0.5 mb-0.5">
          {['Date', 'Flt', 'From', 'To', 'Dep', 'Arr'].map((h) => (
            <span key={h} className="text-[6px] font-bold text-amber-500/20 text-center uppercase">{h}</span>
          ))}
        </div>
        {ROSTER.map((r, i) => (
          <div key={i} className={`grid grid-cols-6 gap-0.5 py-[2px] border-b border-white/[0.02] ${r.lyr ? 'bg-amber-500/[0.02]' : ''}`}>
            <span className="text-[7px] text-slate-500 text-center">{r.d}</span>
            <span className={`text-[7px] text-center font-bold ${r.off || r.lyr ? 'text-slate-600' : 'text-amber-400/60'}`}>{r.f}</span>
            <span className="text-[7px] text-slate-400 text-center">{r.fr}</span>
            <span className="text-[7px] text-slate-400 text-center">{r.to}</span>
            <span className="text-[7px] text-slate-600 text-center">{r.dep}</span>
            <span className="text-[7px] text-slate-600 text-center">{r.arr}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniRoster() {
  return (
    <div className="p-1">
      {ROSTER.slice(0, 5).map((r, i) => (
        <div key={i} className="grid grid-cols-4 gap-1 py-[1px]">
          <span className="text-[7px] text-slate-500 text-center">{r.d}</span>
          <span className={`text-[7px] text-center font-bold ${r.off ? 'text-slate-600' : 'text-amber-400/50'}`}>{r.f}</span>
          <span className="text-[7px] text-slate-400 text-center">{r.fr}</span>
          <span className="text-[7px] text-slate-400 text-center">{r.to}</span>
        </div>
      ))}
    </div>
  );
}
